import apiService from './api';
import type { ApiResponse } from '../types';

/**
 * Time in lieu by agreement (2026-10-08). Overtime is paid unless the employee
 * has asked for time in lieu, accepted the agreement, and someone with a
 * Timesheet HR seat has approved it. Then it is banked hour for hour, from the
 * day of approval until either side ends it. Server: HHNodeServer
 * `timeInLieuAgreementService`, `/api/time-in-lieu`.
 *
 * Fortnightly staff are the exception (2026-10-20): while the organisation's
 * `fortnightly_excess_category` is 'Time in Lieu', their overtime is banked
 * with no agreement unless they opt out.
 */

export type TilStatus = 'pending' | 'approved' | 'declined' | 'cancelled' | 'ended';

export interface TilAgreement {
  id: number;
  user_id: number;
  status: TilStatus;
  agreement_version: string;
  agreement_hash: string;
  agreement_text: string;
  requested_at: string;
  decided_at: string | null;
  decision_note: string | null;
  /** YYYY-MM-DD, set on approval. */
  effective_from: string | null;
  /** YYYY-MM-DD; not in force from this day. */
  ended_on: string | null;
  ended_at: string | null;
  end_reason: string | null;
  employee_name: string | null;
  employee_username: string | null;
  pay_frequency: string | null;
  decided_by_name: string | null;
  ended_by_name: string | null;
  ended_by_employee: boolean | null;
  /** On the HR list only: in force today. */
  in_force?: boolean;
}

export interface TilAgreementText {
  version: string;
  text: string;
  hash: string;
}

export interface MyTilStatus {
  /** What overtime worked today becomes. */
  choice: 'overtime' | 'time_in_lieu';
  /** Fortnightly pay in an organisation that banks it: time in lieu unless opted out. */
  by_default?: boolean;
  /** Their open opt-out from that default: paid overtime from `effective_from`. */
  opt_out?: { id: number; effective_from: string; reason: string | null } | null;
  /** The request waiting for HR, or the agreement in force. */
  open: TilAgreement | null;
  history: TilAgreement[];
  /** The text to read and accept. */
  agreement: TilAgreementText;
}

class TimeInLieuService {
  async getMine(): Promise<ApiResponse<MyTilStatus>> {
    return apiService.get('/time-in-lieu/me');
  }

  /** Ask for time in lieu, accepting exactly the text that was shown. */
  async request(agreement: TilAgreementText): Promise<ApiResponse<TilAgreement>> {
    return apiService.post('/time-in-lieu/me', { version: agreement.version, hash: agreement.hash });
  }

  async cancelRequest(): Promise<ApiResponse<null>> {
    return apiService.delete('/time-in-lieu/me');
  }

  async endMine(reason?: string): Promise<ApiResponse<TilAgreement>> {
    return apiService.post('/time-in-lieu/me/end', { reason: reason || null });
  }

  /** Fortnightly default: paid overtime from today. */
  async optOut(reason?: string): Promise<ApiResponse<MyTilStatus>> {
    return apiService.post('/time-in-lieu/me/opt-out', { reason: reason || null });
  }

  /** Fortnightly default: time in lieu again from today. */
  async optIn(): Promise<ApiResponse<MyTilStatus>> {
    return apiService.delete('/time-in-lieu/me/opt-out');
  }

  /** HR: every request and agreement, optionally for one person. */
  async list(userId?: number): Promise<ApiResponse<TilAgreement[]>> {
    return apiService.get(userId ? `/time-in-lieu?user_id=${userId}` : '/time-in-lieu');
  }

  async decide(id: number, approve: boolean, note?: string): Promise<ApiResponse<TilAgreement>> {
    return apiService.post(`/time-in-lieu/${id}/decide`, { approve, note: note || null });
  }

  async end(id: number, reason?: string): Promise<ApiResponse<TilAgreement>> {
    return apiService.post(`/time-in-lieu/${id}/end`, { reason: reason || null });
  }
}

export const timeInLieuService = new TimeInLieuService();
export default timeInLieuService;
